import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import i18n from 'i18next';
import React from 'react';
import {sortCaseInsensitive, sortTimes} from '../../common/util/SortUtil';
import TableCell from './TableCell';

export function buildTrainingColumns(columnOptions, interimNumber, onDelete, onEdit, hideModificationColumns) {
    return buildColumns(columnOptions, interimNumber, onDelete, onEdit, hideModificationColumns);
}

const tableCell = 'table-cell';

const cellStyleSwitch = (index, rank, isFinal, maxRank) => {
    if (rank === '1') {
        return 'table-cell-fastest';
    }
    if (rank && maxRank && rank === maxRank.toString()) {
        return 'table-cell-slowest';
    }
    if (isFinal) {
        return 'table-cell-final';
    }
    return index % 2 === 1 ? tableCell : 'table-cell-light';
};

const buildInterimColumns = (columnOptions, interimNumber) => {
    if (interimNumber <= 0) {
        return [];
    } else {
        return Array(interimNumber).fill(6).map((interim, index) => {
            const interimTime = {
                Header: `${i18n.t('Training.INTERIM')} ${index + 1}`,
                Cell: props => <div>{props.value || ''}</div>,
                columns: []
            };

            if (columnOptions.find(col => col.accessor === 'interim')?.show) {
                if (columnOptions.find(col => col.accessor === 'section')?.show) {
                    interimTime.columns.push({
                        Header: `${i18n.t('Training.SECTION')} ${index + 1}`,
                        accessor: `section${index + 1}`,
                        Cell: props => <TableCell content={props.value} containerStyle={cellStyleSwitch(index)}/>,
                        sortType: (a, b) => sortTimes(a.original[`section${index + 1}`], b.original[`section${index + 1}`], a.original.idx, b.original.idx)
                    });
                }

                interimTime.columns.push({
                    Header: i18n.t('Training.INTERIM_RANK'),
                    accessor: `interim${index + 1}_rank`,
                    Cell: props => <TableCell content={props.value} containerStyle={cellStyleSwitch(index)}/>,
                    sortType: (a, b) => sortTimes(a.original[`interim${index + 1}`], b.original[`interim${index + 1}`], a.original.idx, b.original.idx)
                });
                interimTime.columns.push({
                    Header: i18n.t('Training.TIME'),
                    accessor: `interim${index + 1}`,
                    Cell: props => <TableCell content={props.value}
                                              containerStyle={cellStyleSwitch(index, props.row.original[`interim${index + 1}_rank`],
                                                  false, props.row.original[`interim${index + 1}_max_rank`])}
                                              contentStyle={'value-time'}/>,
                    sortType: (a, b) => sortTimes(a.original[`interim${index + 1}`], b.original[`interim${index + 1}`], a.original.idx, b.original.idx)
                });
                interimTime.columns.push({
                    Header: i18n.t('Training.DIFFERENCE'),
                    accessor: `interim${index + 1}_diff`,
                    Cell: props => <TableCell content={props.value} containerStyle={cellStyleSwitch(index)} contentStyle={'value-diff'}/>,
                    sortType: (a, b) => sortTimes(a.original[`interim${index + 1}_diff`], b.original[`interim${index + 1}_diff`], a.original.idx, b.original.idx)
                });
            }
            return interimTime;
        });
    }
};

const buildInterimSectionColumns = (columnOptions, interimNumber) => {
    if (interimNumber <= 0 || columnOptions.find(col => col.accessor === 'interim')?.show || !columnOptions.find(col => col.accessor === 'section')?.show) {
        return [];
    } else {
        return Array(interimNumber).fill(6).map((interim, index) => {
            const interimTime = {
                Header: '',
                Cell: props => <div>{props.value || ''}</div>,
                columns: [],
                id: 'interim-section-head'
            };

            interimTime.columns.push({
                Header: `${i18n.t('Training.SECTION')} ${index + 1}`,
                accessor: `section${index + 1}`,
                Cell: props => <TableCell content={props.value} containerStyle={cellStyleSwitch(index)}/>,
                sortType: (a, b) => sortTimes(a.original[`interim${index + 1}`], b.original[`interim${index + 1}`], a.original.idx, b.original.idx)
            });
            return interimTime;
        });
    }
};

const buildFinalColumns = (columnOptions) => {
    const finalColumns = {
        Header: i18n.t('Training.FINAL'),
        columns: []
    };

    if (columnOptions.find(col => col.accessor === 'final')?.show) {
        if (columnOptions.find(col => col.accessor === 'section')?.show) {
            finalColumns.columns.push({
                Header: i18n.t('Training.SECTION_FINAL'),
                accessor: 'section_final',
                Cell: props => <TableCell content={props.value} containerStyle={'table-cell-final-left'}/>,
                sortType: (a, b) => sortTimes(a.original.section_final, b.original.section_final, a.original.idx, b.original.idx)
            });
        }
        finalColumns.columns.push({
            Header: i18n.t('Training.FINAL_RANK'),
            accessor: 'final_rank',
            Cell: props => <TableCell content={props.value} containerStyle={'table-cell-final-left'}/>,
            sortType: (a, b) => sortTimes(a.original.final, b.original.final, a.original.idx, b.original.idx)
        });
        finalColumns.columns.push({
            Header: i18n.t('Training.TIME'),
            accessor: 'final',
            Cell: props => <TableCell content={props.value} containerStyle={cellStyleSwitch(0, props.row.original.final_rank, true, props.row.original.max_final_rank)}
                                      contentStyle={'value-time'}/>,
            sortType: (a, b) => sortTimes(a.original.final, b.original.final, a.original.idx, b.original.idx)
        });
        finalColumns.columns.push({
            Header: i18n.t('Training.DIFFERENCE'),
            accessor: 'final_diff',
            Cell: props => <TableCell content={props.value} containerStyle={'table-cell-final-right'} contentStyle={'value-diff'}/>,
            sortType: (a, b) => sortTimes(a.original.final_diff, b.original.final_diff, a.original.idx, b.original.idx)
        });
    }

    return [finalColumns];
};

const buildFinalSectionColumn = (columnOptions) => {
    const finalSectionColumn = {
        Header: '',
        columns: [],
        id: 'final-section-head'
    };
    if (!columnOptions.find(col => col.accessor === 'final')?.show && columnOptions.find(col => col.accessor === 'section')?.show) {
        finalSectionColumn.columns.push({
            Header: i18n.t('Training.SECTION_FINAL'),
            accessor: 'section_final',
            id: 'final_section',
            Cell: props => <TableCell content={props.value} containerStyle={'table-cell-final-left'}/>,
            sortType: (a, b) => sortTimes(a.original.section_final, b.original.section_final, a.original.idx, b.original.idx)
        });
    }
    return [finalSectionColumn];
};

const buildGearColumns = (columnOptions) => {
    const gearColumns = {
        Header: ' ',
        columns: []
    };

    if (columnOptions.find(col => col.accessor === 'ski')?.show) {
        gearColumns.columns.push({
            Header: i18n.t('Training.SKI'),
            accessor: 'ski',
            Cell: props => <span className="mr-2" data-tip={props.value}>{props.value}</span>,
            sortType: (a, b) => (sortCaseInsensitive(a.original.ski, b.original.ski) === 0)
                ? (a.original.idx >= b.original.idx ? 1 : -1)
                : (sortCaseInsensitive(a.original.ski, b.original.ski) > 0 ? 1 : -1)
        });
    }
    if (columnOptions.find(col => col.accessor === 'boots')?.show) {
        gearColumns.columns.push({
            Header: i18n.t('Training.BOOTS'),
            accessor: 'boots',
            Cell: props => <span className="mr-2" data-tip={props.value}>{props.value}</span>,
            sortType: (a, b) => (sortCaseInsensitive(a.original.boots, b.original.boots) === 0)
                ? (a.original.idx >= b.original.idx ? 1 : -1)
                : (sortCaseInsensitive(a.original.boots, b.original.boots) > 0 ? 1 : -1)
        });
    }
    if (columnOptions.find(col => col.accessor === 'binding')?.show) {
        gearColumns.columns.push({
            Header: i18n.t('Training.BINDING'),
            accessor: 'binding',
            Cell: props => <span className="mr-2" data-tip={props.value}>{props.value}</span>,
            sortType: (a, b) => (sortCaseInsensitive(a.original.binding, b.original.binding) === 0)
                ? (a.original.idx >= b.original.idx ? 1 : -1)
                : (sortCaseInsensitive(a.original.binding, b.original.binding) > 0 ? 1 : -1)
        });
    }
    if (columnOptions.find(col => col.accessor === 'plate')?.show) {
        gearColumns.columns.push({
            Header: i18n.t('Training.PLATE'),
            accessor: 'plate',
            Cell: props => <span className="mr-2" data-tip={props.value}>{props.value}</span>,
            sortType: (a, b) => (sortCaseInsensitive(a.original.plate, b.original.plate) === 0)
                ? (a.original.idx >= b.original.idx ? 1 : -1)
                : (sortCaseInsensitive(a.original.plate, b.original.plate) > 0 ? 1 : -1)
        });
    }
    return [gearColumns];
};

const buildRunColumns = (columnOptions) => {
    const runColums = {
        Header: ' ',
        columns: []
    };
    if (columnOptions.find(col => col.accessor === 'idx')?.show) {
        runColums.columns.push({
            Header: i18n.t('Training.IDX'),
            accessor: 'idx',
            Cell: props => <TableCell content={props.value} containerStyle={tableCell}/>,
            sortType: (a, b) => (a.original.idx >= b.original.idx ? 1 : -1)
        });
    }
    if (columnOptions.find(col => col.accessor === 'run')?.show) {
        runColums.columns.push({
            Header: i18n.t('Training.RUN'),
            accessor: 'run',
            Cell: props => <TableCell content={props.value} containerStyle={tableCell}/>,
            sortType: (a, b) => (a.original.run === b.original.run)
                ? (a.original.idx >= b.original.idx ? 1 : -1)
                : (a.original.run < b.original.run ? -1 : 1)
        });
    }

    if (columnOptions.find(col => col.accessor === 'team')?.show) {
        runColums.columns.push({
            Header: i18n.t('Training.TEAM'),
            accessor: 'team',
            Cell: props => <span className="mr-2" data-tip={props.value}>{props.value}</span>,
            sortType: (a, b) => (sortCaseInsensitive(a.original.team, b.original.team) === 0)
                ? (a.original.idx >= b.original.idx ? 1 : -1)
                : (sortCaseInsensitive(a.original.team, b.original.team) > 0 ? 1 : -1)
        });
    }

    if (columnOptions.find(col => col.accessor === 'athleteShortName')?.show) {
        runColums.columns.push({
            Header: i18n.t('Training.ATHLETE_SHORT_NAME'),
            accessor: 'athleteShortName',
            Cell: props => <span data-tip={props.value}>{props.value}</span>,
            sortType: (a, b) => (sortCaseInsensitive(a.original.athleteShortName, b.original.athleteShortName) === 0)
                ? (a.original.idx >= b.original.idx ? 1 : -1)
                : (sortCaseInsensitive(a.original.athleteShortName, b.original.athleteShortName) > 0 ? 1 : -1)
        });
    }

    if (columnOptions.find(col => col.accessor === 'athleteFullName')?.show) {
        runColums.columns.push({
            Header: i18n.t('Training.ATHLETE_FULL_NAME'),
            accessor: 'athleteFullName',
            Cell: props => <span data-tip={props.value}>{props.value}</span>,
            sortType: (a, b) => (sortCaseInsensitive(a.original.athleteFullName, b.original.athleteFullName) === 0)
                ? (a.original.idx >= b.original.idx ? 1 : -1)
                : (sortCaseInsensitive(a.original.athleteFullName, b.original.athleteFullName) > 0 ? 1 : -1)
        });
    }

    return [runColums];
};

const buildDeleteColumn = (onDelete, hideDeleteColumn) => {
    const deleteColumn = {
        Header: ' ',
        columns: []
    };

    deleteColumn.columns.push(
        {
            Header: '',
            id: 'delete',
            accessor: 'id',
            Cell: props => {
                return (hideDeleteColumn ? '' :
                        <button
                            className="btn btn-transparent-secondary btn-icon"
                            onClick={(event) => {
                                event.stopPropagation();
                                if (onDelete) {
                                    onDelete(props.cell.row.original.id);
                                }
                            }}
                        >
                            {<FontAwesomeIcon icon="trash"/>}
                        </button>
                );
            }
        });

    return [deleteColumn];
};

const buildEditColumn = (onEdit, hideEditColumn) => {
    const editColumn = {
        Header: ' ',
        columns: []
    };

    editColumn.columns.push(
        {
            Header: '',
            id: 'edit',
            accessor: 'id',
            Cell: props => {
                return (hideEditColumn ? '' :
                        <button
                            className="btn btn-transparent-secondary btn-icon"
                            onClick={(event) => {
                                event.stopPropagation();
                                if (onEdit) {
                                    onEdit(props.cell.row.original.id);
                                }
                            }}
                        >
                            {<FontAwesomeIcon icon="pencil-alt"/>}
                        </button>
                );
            }
        });

    return [editColumn];
};

const buildNoteColumn = (columnOptions) => {
    const noteColumn = {
        Header: ' ',
        columns: []
    };

    if (columnOptions.find(col => col.accessor === 'notes')?.show) {
        noteColumn.columns.push({
            Header: i18n.t('Training.NOTES'),
            accessor: 'notes',
            Cell: props => <span className="mr-2" data-tip={props.value}>{props.value}</span>,
            sortType: (a, b) => sortCaseInsensitive(a.original.notes, b.original.notes)
        });
    }

    return [noteColumn];
};

const buildColumns = (columnOptions, interimNumber, onDelete, onEdit, hideModificationColumns) => {
    return [
        ...buildRunColumns(columnOptions),
        ...buildInterimColumns(columnOptions, interimNumber),
        ...buildInterimSectionColumns(columnOptions, interimNumber),
        ...buildFinalColumns(columnOptions),
        ...buildFinalSectionColumn(columnOptions),
        ...buildNoteColumn(columnOptions),
        ...buildGearColumns(columnOptions),
        ...buildEditColumn(onEdit, hideModificationColumns),
        ...buildDeleteColumn(onDelete, hideModificationColumns)
    ].filter(column => column !== undefined);
};

export default buildTrainingColumns;
